import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";

import { useCallback, useEffect, useRef, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";

import { green, grey } from "@mui/material/colors";
import { GlobalState } from "context/GlobalContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CircularProgress, Divider, Switch } from "@mui/material";
import { PimDashboardState } from "context/PimDashboardContext";
import { attrGroupLinkedTables } from "components/PimDashboard/data";
import {
	converAttrToDatagridCol,
	createdByColumns,
	imageTableColumn,
	styleImageColumn,
} from "data/globalData";
import { FileDownloadRounded, FilterAltRounded, PlaylistAddRounded, PublishRounded, UploadRounded } from "@mui/icons-material";
import FileSaver from "file-saver";
import FilterModal from "./FilterModal/FilterModal";
import AddColumnModal from "./AddColumnModal/AddColumnModal";
import { AuthState } from "context/AuthContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import {
	BUY_QTY_SHEET_API,
	buyQtyPublishColumns,
	buyQtySheetAttrStorageStr,
	buyQtySheetFilterValStr,
	buyQtyStageColumn,
	buyQtyStatusColumn,
	buyQtyUserFilterColumns,
	canPublishBuyQtyRow,
	dedupeDataGridColumns,
	filterBuyQtyAttribute,
	resolveBuyQtyAttribute,
} from "./data";
import _ from "lodash";
import EditDetailsModal from "./EditDetailsModal/EditDetailsModal";
import MrpUploadModal from "./UploadModal/UploadModal";
import BulkPublishModal from "./BulkPublishModal/BulkPublishModal";

const colStyle = {
	headerAlign: "center",
	align: "center",
};

export function setColorsCustomFun(prev, newRows) {
	const map = {};

	const result = [...prev, ...newRows]?.reduce((acc, row) => {
		if (map[row.option_id]) return acc;
		else {
			acc.push(row);
			map[row.option_id] = true;
		}
		return acc;
	}, []);
	return result;
}

const BuyQtySheet = ({ onFilterChange, onFilterUiChange }) => {
	const gridRef = useRef();
	const { allAttributes, allAttributesObj } = PimDashboardState();
	const { user } = AuthState();
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const [colors, setColors] = useState([]);
	const [displayImg, setDisplayImg] = useState(true);
	const [tableStatusCount, setTableStatusCount] = useState({
		total: 0,
		pending: 0,
		published: 0,
	});
	const [filterModal, setFilterModal] = useState(false);
	const [addColumnModal, setAddColumnModal] = useState(false);
	const [editModal, setEditModal] = useState(false);
	const [selectedColor, setSelectedColor] = useState({});
	const [uploadModal, setUploadModal] = useState(false);
	const [selectedOptionIds, setSelectedOptionIds] = useState([]);
	const [selectAllFiltered, setSelectAllFiltered] = useState(false);
	const [selectAllCount, setSelectAllCount] = useState(0);
	const [bulkPublishModal, setBulkPublishModal] = useState(false);
	const [bulkPublishPreview, setBulkPublishPreview] = useState([]);
	const [bulkPublishCount, setBulkPublishCount] = useState(0);
	const [bulkPublishTruncated, setBulkPublishTruncated] = useState(false);
	const [bulkPublishLoading, setBulkPublishLoading] = useState(false);
	const publishTargetRef = useRef({ selectAll: false, optionIds: [] });
	const prevSelectionRef = useRef([]);
	const filterRef = useRef({});

	const defColumns = [
		{
			field: "option_id",
			headerName: "Option ID",
			width: 140,
			...colStyle,
		},
	];

	const [styleTablColumns, setStyleTableColumns] = useState(defColumns);

	const [attrColumns, setAttrColumns] = useState(() => {
		if (localStorage.getItem(buyQtySheetAttrStorageStr)) {
			return JSON.parse(localStorage.getItem(buyQtySheetAttrStorageStr))
				?.map((row) => resolveBuyQtyAttribute(row, allAttributesObj))
				?.filter(filterBuyQtyAttribute)
				?.map((row) => converAttrToDatagridCol(row));
		}

		return allAttributes
			?.filter(
				(row) =>
					filterBuyQtyAttribute(row) &&
					(row?.linked_table === attrGroupLinkedTables.style.key ||
						row?.linked_table === attrGroupLinkedTables.color.key),
			)
			?.map((row) => converAttrToDatagridCol(row));
	});

	const [filterColumns, setFilterColumns] = useState(
		// appling filter fron local storage data
		() => {
			let final = [];
			if (localStorage.getItem(buyQtySheetFilterValStr)) {
				const localStorageObj =
					typeof JSON.parse(localStorage.getItem(buyQtySheetFilterValStr)) === "object"
						? JSON.parse(localStorage.getItem(buyQtySheetFilterValStr))
						: {};
				[
					...defColumns,
					...allAttributes,
					...createdByColumns,
					...Object.keys(buyQtyPublishColumns).map((row) => buyQtyPublishColumns[row]),
					...Object.values(buyQtyUserFilterColumns),
				]?.forEach((row) => {
					if (row?.key && localStorageObj?.[row.key] && !final.some((item) => item.key === row.key)) {
						final.push(row);
					}
				});
				return final;
			}
		},
	);
	const [filterColumnVal, setFilterColumnVal] = useState(() => {
		if (localStorage.getItem(buyQtySheetFilterValStr)) {
			return JSON.parse(localStorage.getItem(buyQtySheetFilterValStr));
		} else {
			return {};
		}
	});
	const [offset, setOffset] = useState(0);
	const [recordsLimit] = useState(20);
	const [hasMore, setHasMore] = useState(true);
	const [loading, setLoading] = useState(false);

	filterRef.current = filterColumnVal;
	const selectableOptionIds = (colors || []).filter(canPublishBuyQtyRow).map((row) => row.option_id);
	const selectionModel = selectAllFiltered ? selectableOptionIds : selectedOptionIds;
	const selectedCount = selectAllFiltered ? selectAllCount : selectedOptionIds.length;
	const filterSummary = Object.entries(filterColumnVal || {})
		.filter(([, value]) => value != null && value !== "" && !(Array.isArray(value) && !value.length))
		.map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : value}`)
		.join("; ");

	const clearSelection = () => {
		setSelectedOptionIds([]);
		setSelectAllFiltered(false);
		setSelectAllCount(0);
		prevSelectionRef.current = [];
	};

	const handleCloseFilterModal = () => {
		setFilterModal(false);
	};

	const handleCloseAddColumnModal = () => {
		setAddColumnModal(false);
	};

	const handleFilterClick = useCallback(() => {
		setFilterModal(true);
	}, []);

	const activeFilterCount = filterColumns?.length || 0;

	useEffect(() => {
		onFilterUiChange?.({ open: handleFilterClick, count: activeFilterCount });
	}, [onFilterUiChange, handleFilterClick, activeFilterCount]);

	useEffect(() => {
		onFilterChange?.({ filter: filterColumnVal || {}, search: "" });
	}, [filterColumnVal, onFilterChange]);

	const handleAddColumnClick = () => {
		setAddColumnModal(true);
	};

	const reloadColors = async () => {
		setLoadingOpen(true);
		clearSelection();
		setOffset(0);
		setHasMore(true);
		try {
			const extraColumns = attrColumns?.reduce((acc, row) => {
				acc[row.field] = true;
				return acc;
			}, {});
			const { data } = await axios.post(`${BUY_QTY_SHEET_API}/get_colors/0`, {
				filter: filterColumnVal,
				extraColumns,
				limit: recordsLimit,
			});
			setColors(data.result.colors);
			setTableStatusCount(data.result.count[0]);
			setOffset(recordsLimit);
			setLoadingOpen(false);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error) });
			setLoadingOpen(false);
		}
	};

	const fetchColors = async (offset, limit) => {
		try {
			const extraColumns = attrColumns?.reduce((acc, row) => {
				acc[row.field] = true;
				return acc;
			}, {});

			const { data } = await axios.post(`${BUY_QTY_SHEET_API}/get_colors/${offset}`, {
				filter: filterColumnVal,
				extraColumns,
				limit,
			});
			setColors((prev) => setColorsCustomFun(prev, data.result.colors));
			setTableStatusCount(data.result.count[0]);
			setOffset((prev) => prev + recordsLimit);
			return data.result || [];
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error) });
			return [];
		}
	};

	const loadMoreData = useCallback(
		_.throttle(async () => {
			if (loading || !hasMore) return;
			if (colors?.length > 100) return;
			setLoading(true);

			const newRows = await fetchColors(offset, recordsLimit);

			if (newRows.colors?.length < recordsLimit) setHasMore(false);
			setLoading(false);
		}, 2000),
		[loading, hasMore, offset],
	);

	useEffect(() => {
		const gridElement = gridRef.current;
		if (!gridElement) return;

		const scrollContainer = gridElement.querySelector(".MuiDataGrid-virtualScroller");

		if (!scrollContainer) return;

		const handleScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
			if (scrollHeight - scrollTop <= clientHeight + 100) {
				loadMoreData();
			}
		};

		scrollContainer.addEventListener("scroll", handleScroll);

		return () => {
			scrollContainer.removeEventListener("scroll", handleScroll);
		};
	}, [loadMoreData]);
	useEffect(() => {
		const fetchInitialColors = async () => {
			clearSelection();
			setLoadingOpen(true);
			setStyleTableColumns(
				dedupeDataGridColumns([
					buyQtyStatusColumn(),
					buyQtyStageColumn(),
					imageTableColumn(styleImageColumn),
					...attrColumns,
					...defColumns,
					...Object.keys(buyQtyPublishColumns)
						.filter((key) => key !== "buy_qty_status")
						.map((row) => converAttrToDatagridCol(buyQtyPublishColumns[row])),
				]),
			);
			try {
				const result = await fetchColors(0, recordsLimit);
				setColors(result.colors);
				if (result?.count?.[0]) setTableStatusCount(result?.count?.[0]);
				setOffset((prev) => prev + recordsLimit);
				setHasMore(result?.colors?.length === recordsLimit);
				setLoadingOpen(false);
			} catch (error) {
				setSnackBarOpen(true);
				setSnackBar({ ...snackBar, severity: "error", message: parseErrorMessage(error) });
				setLoadingOpen(false);
			}
		};

		fetchInitialColors();
	}, [filterColumns, attrColumns]);

	const navigate = useNavigate();

	const handleImageSwitchChange = (checked) => {
		setDisplayImg(checked);
	};

	const handleColorSelect = async (data) => {
		setSelectedColor(data);
		setEditModal(true);
	};

	const selectedColorIndexRaw = colors?.findIndex((row) => row.option_id === selectedColor?.option_id) ?? -1;
	const selectedColorIndex = selectedColorIndexRaw >= 0 ? selectedColorIndexRaw : 0;

	const handleNextColor = () => {
		if (selectedColorIndex >= 0 && selectedColorIndex < colors.length - 1) {
			setSelectedColor(colors[selectedColorIndex + 1]);
		}
	};

	const handlePrevColor = () => {
		if (selectedColorIndex > 0) {
			setSelectedColor(colors[selectedColorIndex - 1]);
		}
	};
	const handleDownloadClick = async () => {
		setLoadingOpen(true);
		try {
			await axios({
				url: `${BUY_QTY_SHEET_API}/download_buy_qty_sheet`,
				method: "POST",
				responseType: "blob",
				data: { filter: filterColumnVal },
			}).then((res) => {
				const url = URL.createObjectURL(res.data);
				FileSaver.saveAs(url, `buyQtySheet.xlsx`);
				setLoadingOpen(false);
			});
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrorMessage(error) });
			setLoadingOpen(false);
		}
	};

	const handleUploadSave = async (uploadResult) => {
		if (!uploadResult || uploadResult === false) return false;

		if (uploadResult.status === "error") {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: uploadResult.message || "Upload failed",
				severity: "error",
			});
			return uploadResult;
		}

		const successful = uploadResult?.result?.successful ?? 0;
		const failed = uploadResult?.result?.failed ?? 0;
		const uploadedRows = uploadResult?.result?.uploaded_rows;
		const errorRows = uploadResult?.result?.error_rows;

		setSnackBarOpen(true);
		setSnackBar({
			...snackBar,
			message:
				failed > 0
					? `${successful} color(s) uploaded, ${failed} failed${
							uploadedRows != null ? ` · ${uploadedRows} valid row(s), ${errorRows} error row(s)` : ""
						}`
					: `${successful} color(s) uploaded successfully${
							uploadedRows != null ? ` · ${uploadedRows} valid row(s)` : ""
						}`,
			severity: failed > 0 ? "warning" : "success",
		});
		if (successful > 0) reloadColors();
		return uploadResult;
	};

	const handleColorUpdate = () => {
		reloadColors();
	};

	const openPublishPreview = async ({ selectAll = false, optionIds = [] } = {}) => {
		const filter = filterRef.current || {};
		publishTargetRef.current = { selectAll, optionIds };
		setBulkPublishLoading(true);
		setLoadingOpen(true);
		try {
			const { data } = await axios.post(`${BUY_QTY_SHEET_API}/bulk_publish_preview`, {
				select_all: selectAll,
				option_ids: selectAll ? undefined : optionIds,
				filter: selectAll ? filter : undefined,
			});
			setBulkPublishPreview(data?.result?.colors || []);
			setBulkPublishCount(data?.result?.count || 0);
			setBulkPublishTruncated(!!data?.result?.truncated);
			setBulkPublishModal(true);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar((prev) => ({
				...prev,
				severity: "error",
				message: parseErrorMessage(error),
			}));
		} finally {
			setBulkPublishLoading(false);
			setLoadingOpen(false);
		}
	};

	const handleSelectAllFiltered = async () => {
		setLoadingOpen(true);
		try {
			const { data } = await axios.post(`${BUY_QTY_SHEET_API}/bulk_publish_preview`, {
				select_all: true,
				filter: filterRef.current || {},
			});
			const colorsToPublish = data?.result?.colors || [];
			const count = data?.result?.count || colorsToPublish.length;
			setSelectAllFiltered(true);
			setSelectAllCount(count);
			setSelectedOptionIds(selectableOptionIds);
			prevSelectionRef.current = selectableOptionIds;
			setSnackBarOpen(true);
			setSnackBar((prev) => ({
				...prev,
				severity: "info",
				message: `${count} color(s) with total qty > 0 selected from current filter`,
			}));
		} catch (error) {
			clearSelection();
			setSnackBarOpen(true);
			setSnackBar((prev) => ({
				...prev,
				severity: "error",
				message: parseErrorMessage(error),
			}));
		} finally {
			setLoadingOpen(false);
		}
	};

	const normalizeSelectionModel = (newSelectionModel) => {
		if (Array.isArray(newSelectionModel)) return newSelectionModel.map(String);
		if (newSelectionModel instanceof Set) return [...newSelectionModel].map(String);
		if (newSelectionModel?.ids instanceof Set) return [...newSelectionModel.ids].map(String);
		if (Array.isArray(newSelectionModel?.ids)) return newSelectionModel.ids.map(String);
		return [];
	};

	const handleSelectionChange = (newSelectionModel) => {
		const nextSelection = normalizeSelectionModel(newSelectionModel);
		const prevSelection = prevSelectionRef.current;
		const selectableOnPage = selectableOptionIds;

		if (nextSelection.length === 0) {
			clearSelection();
			return;
		}

		const selectedAllLoaded =
			selectableOnPage.length > 0 &&
			nextSelection.length === selectableOnPage.length &&
			selectableOnPage.every((id) => nextSelection.includes(id));

		const previouslyHadAllLoaded =
			selectableOnPage.length > 0 &&
			prevSelection.length === selectableOnPage.length &&
			selectableOnPage.every((id) => prevSelection.includes(id));

		if (selectedAllLoaded && !previouslyHadAllLoaded && !selectAllFiltered) {
			handleSelectAllFiltered();
			return;
		}

		if (selectAllFiltered && !selectedAllLoaded) {
			setSelectAllFiltered(false);
			setSelectAllCount(0);
		}

		setSelectedOptionIds(nextSelection);
		prevSelectionRef.current = nextSelection;
	};

	const handleBulkPublishClick = async () => {
		await openPublishPreview({
			selectAll: selectAllFiltered,
			optionIds: selectAllFiltered ? [] : selectedOptionIds,
		});
	};

	const handleConfirmBulkPublish = async () => {
		const { selectAll, optionIds } = publishTargetRef.current;
		setBulkPublishLoading(true);
		setLoadingOpen(true);
		try {
			const { data } = await axios.post(`${BUY_QTY_SHEET_API}/bulk_publish`, {
				select_all: selectAll,
				option_ids: selectAll ? undefined : optionIds,
				filter: selectAll ? filterRef.current || {} : undefined,
			});
			setBulkPublishModal(false);
			clearSelection();
			setSnackBarOpen(true);
			setSnackBar((prev) => ({
				...prev,
				severity: "success",
				message: data?.message || "Colors published successfully",
			}));
			reloadColors();
		} catch (error) {
			const errorData = error?.response?.data;
			setSnackBarOpen(true);
			setSnackBar((prev) => ({
				...prev,
				severity: "error",
				message: errorData?.message || parseErrorMessage(error),
			}));
		} finally {
			setBulkPublishLoading(false);
			setLoadingOpen(false);
		}
	};

	return (
		<Stack width="100%" pt={1}>
			<Box display="flex" justifyContent="space-around">
				{/* left section */}
				<Stack spacing={1} width="28%">
					<Box display="flex" columnGap={1} pt={1} flexWrap="wrap" alignItems="center" rowGap={"5px"}>
						<Button
							variant="contained"
							size="small"
							startIcon={<FilterAltRounded />}
							onClick={handleFilterClick}
							sx={{ textTransform: "capitalize" }}>
							Filter
						</Button>
						:
						{!filterColumns?.length && (
							<Typography variant="body1" fontSize={16} color={grey[700]}>
								None
							</Typography>
						)}
						{filterColumns?.map((row) => {
							return (
								<Chip
									label={`${row.name} : ${
										Array.isArray(filterColumnVal[row?.key])
											? filterColumnVal[row?.key]?.join(", ")
											: filterColumnVal?.[row?.key]
									}`}
									size="small"
								/>
							);
						})}
					</Box>
					<Box>
						<Button
							variant="contained"
							sx={{ textTransform: "capitalize", mr: 1 }}
							size="small"
							onClick={handleAddColumnClick}
							startIcon={<PlaylistAddRounded />}>
							Manage Columns
						</Button>
						<Button
							variant="contained"
							size="small"
							sx={{ textTransform: "capitalize" }}
							onClick={handleDownloadClick}
							startIcon={<FileDownloadRounded />}>
							Download
						</Button>
					</Box>
				</Stack>
				{/* right section */}
				<Box width="68%"></Box>
			</Box>
			<Box display="flex" justifyContent="space-between" alignItems="center" pt={1}>
				<Box display="flex" alignItems="center">
					<Typography variant="h6">Images</Typography>
					<Switch checked={displayImg} onChange={(e) => handleImageSwitchChange(e.target.checked)} sx={{ mt: 1 }} />
				</Box>
				<Box display="flex" alignItems="center" justifyContent="space-between" width="100%" columnGap={2}>
					<Box display="flex" alignItems="center" columnGap={1} flexWrap="wrap">
						<Typography variant="body1">
							Total: <span style={{ color: grey[700] }}>{tableStatusCount.total}</span>
						</Typography>
						<Divider orientation="vertical" flexItem />
						<Typography variant="body1">
							Published: <span style={{ color: green[600] }}>{tableStatusCount.published}</span>
						</Typography>
						<Divider orientation="vertical" flexItem />
						<Typography variant="body1" fontWeight={600}>
							Pending: <span style={{ color: "red" }}>{tableStatusCount.pending}</span>
						</Typography>
						{selectedCount > 0 && (
							<>
								<Divider orientation="vertical" flexItem />
								<Typography variant="body1">
									Selected:{" "}
									<span style={{ color: grey[700] }}>
										{selectAllFiltered ? `${selectedCount} (all matching filter)` : selectedCount}
									</span>
								</Typography>
							</>
						)}
					</Box>
					<Box display="flex" alignItems="center" columnGap={1}>
						{selectedCount > 0 && (
							<Button
								variant="contained"
								color="success"
								size="small"
								sx={{ textTransform: "capitalize" }}
								onClick={handleBulkPublishClick}
								startIcon={<PublishRounded />}>
								Bulk Publish
							</Button>
						)}
						<Button
							variant="contained"
							size="small"
							sx={{ textTransform: "capitalize" }}
							onClick={() => setUploadModal(true)}
							startIcon={<UploadRounded />}>
							Upload Data
						</Button>
					</Box>
				</Box>
			</Box>

			<Box height="60vh" ref={gridRef}>
				<DataGrid
					rows={colors ? colors : []}
					key={displayImg}
					columns={styleTablColumns ?? []}
					sx={{
						// minWidth: "100%"
						border: "none",
						"& .MuiDataGrid-row": {
							// borderBottom: "1px solid lightgray",
							cursor: "pointer",
							border: "none",
						},
						"& .MuiDataGrid-columnHeader": {
							cursor: "pointer",
							background: grey[300],
						},
						"& .MuiDataGrid-columnHeaders": {
							background: grey[300],
							// mb: 2,
							border: "none",
							borderRadius: 1,
							// padding: "10px 0px",
						},
						"& .MuiDataGrid-footer": {
							background: grey[300],
							// mb: 2,
							border: "none",
							borderRadius: 1,
							// padding: "10px 0px",
						},
					}}
					columnHeaderHeight={35}
					rowHeight={displayImg ? 70 : 40}
					getRowId={(row) => row?.option_id}
					onRowClick={(params, e) => {
						if (e?.target?.closest?.(".MuiDataGrid-cellCheckbox, .MuiCheckbox-root")) return;
						handleColorSelect(params.row);
					}}
					initialState={{
						columns: {
							columnVisibilityModel: {
								[styleImageColumn.key]: displayImg,
							},
						},
					}}
					disableRowSelectionOnClick
					hideFooter={true}
					pagination={false}
					// disableColumnFilter
					disableColumnSelector
					disableAutosize
					checkboxSelection
					isRowSelectable={(params) => canPublishBuyQtyRow(params.row)}
					rowSelectionModel={selectionModel}
					onRowSelectionModelChange={handleSelectionChange}
				/>
				{/* <Box sx={{ minHeight: "20px" }}> */}
				{loading && (
					<Box display="flex" sx={{ background: grey[400], borderRadius: 2, p: 1 }} alignItems="center">
						<Typography variant="body2" color={grey[900]}>
							Loading...
						</Typography>
						<CircularProgress size="18px" sx={{ transform: "scale(0.4)" }} />
					</Box>
				)}
			</Box>

			<FilterModal
				modal={filterModal}
				handleCloseModal={handleCloseFilterModal}
				filterColumns={filterColumns}
				setFilterColumns={setFilterColumns}
				filterColumnVal={filterColumnVal}
				setFilterColumnVal={setFilterColumnVal}
				localStorageStr={buyQtySheetFilterValStr}
			/>
			<AddColumnModal
				modal={addColumnModal}
				handleCloseModal={handleCloseAddColumnModal}
				attrColumns={attrColumns}
				setAttrColumns={setAttrColumns}
				localStorageStr={buyQtySheetAttrStorageStr}
			/>
			{editModal && (
				<EditDetailsModal
					modal={editModal}
					setModal={setEditModal}
					selectedColor={selectedColor}
					setSelectedColor={setSelectedColor}
					saveCallback={handleColorUpdate}
					currentIndex={selectedColorIndex}
					totalCount={colors?.length ?? 0}
					onPrev={handlePrevColor}
					onNext={handleNextColor}
				/>
			)}
			<MrpUploadModal modal={uploadModal} setModal={setUploadModal} callbackFun={handleUploadSave} />
			<BulkPublishModal
				modal={bulkPublishModal}
				setModal={setBulkPublishModal}
				previewColors={bulkPublishPreview}
				publishCount={bulkPublishCount}
				truncated={bulkPublishTruncated}
				selectAllFiltered={selectAllFiltered}
				filterSummary={filterSummary}
				loading={bulkPublishLoading}
				onConfirm={handleConfirmBulkPublish}
			/>
		</Stack>
	);
};

export default BuyQtySheet;
